import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  signal,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {MatSidenavModule} from '@angular/material/sidenav';
import {SidenavService} from '@shared/services/sidenav.service';
import {MatCardModule} from '@angular/material/card';
import {BoxComponent} from '@shared/components/box/box.component';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTabChangeEvent, MatTabsModule} from '@angular/material/tabs';
import { CommonModule } from '@angular/common';
import {MatMenuModule} from '@angular/material/menu';
import {MatInputModule} from '@angular/material/input';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {
  All_Notifications_PlaceHolder,
  InAppNotification,
  InAppNotificationModel,
  InAppNotificationsPayload,
  NotificationRecipientStatus,
  tokenValueMap,
  UnRead_Notifications_PlaceHolder
} from '@shared/models/app-notifications.model';
import {BehaviorSubject, debounceTime, distinctUntilChanged, filter, pairwise, Subscription, switchMap} from 'rxjs';
import {AppNotificationService} from '@shared/services/app-notification.service';
import {BaseComponent} from '../base-component';
import {MatTooltipModule} from '@angular/material/tooltip';

import {AppNotificationsContentComponent} from '../app-notifications-content/app-notifications-content.component';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import { provideTranslocoScope, TranslocoPipe } from '@jsverse/transloco';

export enum AppNotificationTabEnum {
  All = 0,
  Unread = 1
}
interface NotificationTabsInfo { name: string; itemType: AppNotificationTabEnum | null }
@Component({
    selector: 'app-notifications',
    templateUrl: './app-notifications.component.html',
    styleUrls: ['./app-notifications.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [provideTranslocoScope('sharedNav')],
    imports: [
    MatSidenavModule,
    MatCardModule,
    BoxComponent,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatMenuModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatTooltipModule,
    CommonModule,
    AppNotificationsContentComponent,
    TranslocoPipe
]
})
export class AppNotificationsComponent extends BaseComponent implements OnDestroy {
  public matomoClass = 'matomo_notifications';
  public searchTerms = '';
  public activityType = AppNotificationTabEnum;
  public matomoSafeHtml?: SafeHtml;
  public selectedTab = signal(AppNotificationTabEnum.All);
  public isShowOnlyUnRead = signal(false);
  public readonly inAppNotificationsRecentData = signal<InAppNotification[]>([]);
  public readonly inAppNotificationsOlderData = signal<InAppNotification[]>([]);
  public readonly notificationAggregator = signal('Last 7 Days');
  public readonly notificationPlaceHolder = signal(All_Notifications_PlaceHolder);
  public unReadNotificationCount = signal<number>(0);
  public readonly enableSearch = signal(true);
  public inAppNotificationTabs: NotificationTabsInfo[] = [
    { name: 'All', itemType: this.activityType.All },
    { name: 'Unread', itemType: this.activityType.Unread }
  ];
  public notificationSearchValueSubject = new BehaviorSubject('');
  public notificationSearchValue = signal('');
  public readonly displaySearchBar = signal(false);
  private notificationSubscription?: Subscription;
  private _open = false;
  @Input() set isOpen(isOpen: boolean) {
    this._open = isOpen;
    this.selectedTab.set(AppNotificationTabEnum.All);
    if (isOpen) {
      this.sidenavService.open(this.sidenav);
      this.getAppNotifications(this.getNotificationSearchPayload());
    } else {
      this.sidenavService.close();
    }
  }

  get isOpen() {
    return this._open;
  }
  @Output() isOpenChange = new EventEmitter<boolean>();

  @ViewChild('notificationTemplate') public sidenav!: TemplateRef<any>;
  constructor(
    public sidenavService: SidenavService,
    public appNotificationService: AppNotificationService,
    private sanitizer: DomSanitizer,
  ) {
    super();
    this.notificationSubscription = this.appNotificationService.startPolling(this.getNotificationSearchPayload()).subscribe(result => {
      this.appNotificationService.isEnableNotificationBellIcon.next(
        result.hasUnread
      );

      if (this.isOpen && result?.hasRecent) {
        this.getAppNotifications(this.getNotificationSearchPayload());
      }
    });

    this.notificationSearchValueSubject
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        pairwise(),
        filter(([prev, next]: [string, string]) => prev !== next),
        switchMap(() => this.appNotificationService.getInAppNotifications(this.getNotificationSearchPayload())),
      ).subscribe(data => {
        this.onGetNotifications(data);
      });

    this.appNotificationService.reloadNotifications$.subscribe((isReload: boolean) => {
      if (isReload) {
        this.getAppNotifications(this.getNotificationSearchPayload());
        this.appNotificationService.reloadNotifications.next(false);
      }
    });
  }

  public close() {
    this.sidenavService.close();
    this.selectedTab.set(AppNotificationTabEnum.All);
    this.notificationSearchValue.set('');
    this.isOpenChange.emit(false);
  }

  public onTabChange(event: MatTabChangeEvent) {
    this.selectedTab.set(Number(event.index));
    if (this.selectedTab() === AppNotificationTabEnum.All)
      this.notificationPlaceHolder.set(All_Notifications_PlaceHolder);
    else
      this.notificationPlaceHolder.set(UnRead_Notifications_PlaceHolder);
    this.getAppNotifications(this.getNotificationSearchPayload());
  }

  private setNotifications(data: InAppNotificationModel) {
    this.inAppNotificationsRecentData.set(this.templatePlaceHoldersReplace(data.recentNotifications as InAppNotification[]));
    this.inAppNotificationsOlderData.set(this.templatePlaceHoldersReplace(data.olderNotifications as InAppNotification[]));
    this.unReadNotificationCount.set(data.totalUnreadCount ?? 0);
    this.appNotificationService.isEnableNotificationBellIcon.next(this.unReadNotificationCount() > 0);
    this.displaySearchBar.set(this.showSearchBar());
  }

  private getAppNotifications(notificationPayload: InAppNotificationsPayload) {
    this.appNotificationService.getInAppNotifications(notificationPayload).subscribe(data => {
      this.onGetNotifications(data);
    });
  }

  private onGetNotifications(data: InAppNotificationModel) {
    this.appNotificationService.lastSyncedTime.next(new Date());
    this.setNotifications(data);
  }

  private templatePlaceHoldersReplace(notificationData: InAppNotification[]) {
    notificationData?.forEach(notification => {
      let template = notification.template;
      for (const token in tokenValueMap) {
        if (Object.prototype.hasOwnProperty.call(tokenValueMap, token)) {
          const value = notification.content[tokenValueMap[token]];
          template = template.replace(token, value);
        }
      }
      notification.template = template;
      notification.notificationSafeHtml = this.sanitizer.bypassSecurityTrustHtml(template);
    });
    return notificationData;

  }

  private getNotificationSearchPayload(): InAppNotificationsPayload {
    const notificationPayload: InAppNotificationsPayload = {
      searchTerm: this.notificationSearchValue(),
      lastSyncedTime: null,
      recipientStatus: this.selectedTab() === AppNotificationTabEnum.Unread ? NotificationRecipientStatus.Unread : null
    };
    return notificationPayload;
  }

  public onMarkAsAllNotifications() {
    this.appNotificationService.markAsReadAllInAppNotifications(this.getNotificationSearchPayload());
  }

  public onDeleteAllNotifications() {
    this.appNotificationService.deleteAll(this.getNotificationSearchPayload());
  }

  public onDeleteNotification(notificationId: number) {
    this.appNotificationService.deleteNotification(notificationId);
  }

  public onMarkAsReadNotification(notificationId: number) {
    this.appNotificationService.markasReadNotification(notificationId);
  }

  public onMarkAsUnReadNotification(notificationId: number) {
    this.appNotificationService.markasUnReadNotification(notificationId);
  }

  public onSearchNotifications(value: string) {
    this.notificationSearchValue.set(value);
    this.notificationSearchValueSubject.next(value);
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.notificationSubscription?.unsubscribe();
    this.stopNotificationTimer();
    this.isOpenChange.emit(false);
  }

  private stopNotificationTimer() {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
  }

  private showSearchBar(): boolean {
    return (this.selectedTab() == AppNotificationTabEnum.All && ((this.inAppNotificationsRecentData()?.length ?? 0) + (this.inAppNotificationsOlderData()?.length ?? 0) > 0) || this.notificationSearchValue() !== '' )
      || this.unReadNotificationCount() > 0;
  }
}
